/**
 * チームの担当（DLE / DLE サブ / DLD / TechLead / チームリーダー）の API クライアント。
 *
 * **選べるチーム・事業を画面に写さない。** 値の正本は Backlog のカスタムフィールドで、
 * 一覧の返りに `teamOptions` / `businessOptions` として入ってくる。画面に enum を置くと
 * Backlog 側で選択肢が増えたときに黙ってずれる。
 *
 * 弾かれたときは `ApiError.message` をそのまま出す（API が返した文言がそのまま直し方になる）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チームの担当を一覧・設定・削除する API クライアント
 * @graph-connects /team-assignments [calls] チームの担当 API
 */

import { request } from './_request.js';

/** 担当 1 行。 @graph-connects none */
export interface TeamAssignment {
  business: string;
  team: string;
  div: string;
  group: string;
  leader: string;
  dle: string[];
  dleSub: string[];
  dld: string[];
  techLead: string;
  /**
   * Workspace の在籍者数。**0 なら今はもう無いチームの担当だけが残っている**可能性がある。
   * 一覧は既定でこの行を畳む。
   */
  currentMemberCount: number;
  updatedAt: string;
  updatedBy: string;
}

/** 在籍者が居るのに担当の行が無いチーム。 @graph-connects none */
export interface MissingTeam {
  team: string;
  currentMemberCount: number;
}

/** 当てられなかった理由。文言は理由ごとに違う。 @graph-connects none */
export type UnmappedOrgReason =
  | 'divisionNotSet'
  | 'teamNotSet'
  | 'employmentCategory'
  | 'unknownDivision'
  | 'otherNaming'
  | 'noBacklogOption';

/** どこを直すか。節の見出しはこれで決める。 @graph-connects none */
export type UnmappedOrgFixOwner = 'workspace' | 'backlog' | 'cortex';

/** チームに当てられなかった Workspace の組織（すべて人が直せるもの）。 @graph-connects none */
export interface UnmappedOrg {
  division: string | null;
  groupName: string | null;
  teamName: string | null;
  memberCount: number;
  reason: UnmappedOrgReason;
  /** 直す先。並び順も API が決めるので、画面は順序表を持たない */
  fixOwner: UnmappedOrgFixOwner;
}

/** 責任チームを持たないと記録済みの開発組織（`ACE` 配下）。 @graph-connects none */
export interface DevOrg {
  division: string | null;
  groupName: string | null;
  teamName: string | null;
  memberCount: number;
}

/** 一覧の返り（突き合わせの結果と選択肢つき）。 @graph-connects none */
export interface TeamAssignmentList {
  assignments: TeamAssignment[];
  missingTeams: MissingTeam[];
  unmappedOrgs: UnmappedOrg[];
  devOrgs: DevOrg[];
  teamOptions: string[];
  businessOptions: string[];
  /** 担当に選べる人（在籍者の nickName）。画面はここから選ばせる＝手入力させない */
  memberOptions: string[];
}

/** 設定で送る 1 行。未指定の欄は送らない（空文字で上書きしないため）。 @graph-connects none */
export interface TeamAssignmentInput {
  business: string;
  team: string;
  div?: string;
  group?: string;
  leader?: string;
  dle?: string[];
  dleSub?: string[];
  dld?: string[];
  techLead?: string;
}

/** チームの担当 API。 @graph-connects none */
export const teamAssignmentsApi = {
  /** @graph-connects /team-assignments [calls] 担当の一覧取得 */
  list: (): Promise<TeamAssignmentList> => request('/team-assignments'),

  /** @graph-connects /team-assignments [calls] 担当の登録・更新 */
  upsert: (input: TeamAssignmentInput): Promise<TeamAssignment> =>
    request('/team-assignments', { method: 'PUT', body: JSON.stringify(input) }),

  /** @graph-connects /team-assignments [calls] 担当の削除 */
  remove: (business: string, team: string): Promise<{ deleted: boolean }> =>
    request(
      `/team-assignments?business=${encodeURIComponent(business)}&team=${encodeURIComponent(team)}`,
      { method: 'DELETE' },
    ),
};
